// --- React
import React, {useRef} from 'react';
// --- UI Components
import {Col, Row} from 'antd';
import Typography from '@dtci/spellbook/Typography';
import ReactHtmlParser from 'react-html-parser';
// --- Utilities
import moment from 'moment';
import DOMPurify from 'dompurify';
// --- Styles
import '../../memo-form/styles.scss';
import {useLoading} from "../../../../utils/hooks/useLoading";
import {datastageApi} from "../../../../api";
import {saveAs} from 'file-saver';
import {tableOfContents} from "../../../../utils/clearanceReport";
import {formatMemoTitleDisplay} from '../../utils';

// CONSTANTS for legal header and notice
const legalHeader = `PRIVILEGED AND CONFIDENTIAL<br />
SUBJECT TO ATTORNEY-CLIENT AND ATTORNEY WORK PRODUCT PRIVILEGES<br />
DO NOT DUPLICATE - DO NOT CIRCULATE<br /><br />`;

/**
 * ClearanceReportCard Component
 *
 * Displays a Clearance Memo Report for media distribution rights.
 * It includes memo table of contents, legal disclaimers, and a structured contract information layout.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.treeData - Array containing memo sections with content and attachments
 * @param {Object} props.memoDetails - Clearance memo/product details (e.g., title, release date, contractual parties)
 * @param {Object} props.sectionRefs - References for scrolling to specific memo sections
 * @returns {JSX.Element} A formatted clearance report display
 */
export function ClearanceReportCard({treeData, title, memoDetails, sectionRefs}) {

    const {setLoadingTrue, setLoadingFalse} = useLoading();

    // Reference for scrolling to sections when selecting tree item
    const contentRef = useRef(null);

    const reportTitle = memoDetails?.productTitle || memoDetails?.title || title?.title;

    // Product details for display
    const productDetails = [
        {label: 'IRD', value: `(${memoDetails?.releaseDate?.substring(0, 4) || ''})`},
        {label: 'Title', value: formatMemoTitleDisplay(reportTitle)},
        {label: 'Type', value: memoDetails?.productClass || memoDetails?.productType},
        {label: 'Disney Owned', value: memoDetails?.productSource === 'PRODUCED' ? 'Yes' : 'No'},
        {label: 'Prepared By', value: memoDetails?.firstPreparedBy},
        {
            label: 'Last Reviewed',
            value: moment(memoDetails?.lastRevisionDate).format('DD/MMM/YYYY'),
        },
        {label: 'Disney Entity', value: memoDetails?.siteAccess},
        {
            label: 'Contractual Party',
            value: memoDetails?.clearanceMemoContractualParties?.map((party, index) => (
                <div key={index}>
                    {party?.contact?.name} -- {party?.contractualPartyType.description}
                </div>
            )),
        },
    ];


    const downloadClearanceMemoReport = async () => {
        setLoadingTrue();
        const res = await datastageApi.exportClearanceMemoPDF(
            title?.foxFinancialTitleId, title?.id, false, reportTitle)
        saveAs(res.data, title?.title + ' Report.pdf');
        setLoadingFalse();
    };

    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    const reportTOC = tableOfContents(treeData);

   function renderSection(section, indent = 0) {
        const sectionId = section?.clearanceMemoSection?.id;
        const hasBody = !!section?.clearanceMemoSection?.body;
        const children = section?.children || [];

        return (
            <div
                key={sectionId}
                ref={el => sectionRefs.current[sectionId] = el}
                style={{ marginLeft: 20 + indent * 20, paddingBottom: 24 }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24 }}>
                    <span style={{ width: 40, textAlign: 'left', flexShrink: 0 }}>
                        {section.position}
                    </span>
                    <span style={{ marginLeft: 50, fontSize: 15 }}>
                        {hasBody
                            ? ReactHtmlParser(DOMPurify.sanitize(section?.clearanceMemoSection.body))
                            : (
                                <span style={{
                                    textTransform: 'uppercase',
                                    fontWeight: 'bold',
                                    textDecoration: 'underline'
                                }}>
                                    {section?.clearanceMemoSection.title}
                                </span>
                            )
                        }
                        {section?.clearanceMemoSection?.isPublic &&
                            section?.clearanceMemoSection?.attachments?.map((doc, docIndex) => (
                                <a
                                    key={docIndex}
                                    onClick={e => {
                                        e.preventDefault();
                                        downloadAttachment(doc?.rightslineId, doc?.attachmentName);
                                    }}
                                    style={{
                                        fontSize: 16,
                                        display: 'block',
                                        marginTop: 4,
                                        marginBottom: 4,
                                        color: '#3611c8',
                                        marginLeft: 0,
                                    }}
                                >
                                    {doc.attachmentName}
                                </a>
                            ))
                        }
                    </span>
                </div>
                {children.map(child => renderSection(child, indent + 1))}
            </div>
        );
    }
    return (
        <div ref={contentRef} style={{padding: 20, background: '#fff'}}>
            {/* PDF Link for exporting the Clearance Memo Report */}
            {memoDetails?.productId && (
                <a
                    onClick={downloadClearanceMemoReport}
                    style={{
                        fontSize: 14,
                        color: '#3611c8',
                        position: 'absolute',
                        top: 40,
                        right: 30,
                        cursor: 'pointer',
                    }}
                >
                    PDF
                </a>
            )}

            {/* Clearance Memo Report Title */}
            <Typography variant="T60" style={{textAlign: 'center'}}>
                Legal Rights Clearance Report
            </Typography>

            {/* Confidential Disclaimer */}
            <Typography variant="T20" style={{textAlign: 'center', textDecoration: 'underline'}}>
                {ReactHtmlParser(DOMPurify.sanitize(legalHeader))}
            </Typography>

            {/* Product Details */}
            {productDetails.map(({label, value}, index) => (
                <Row key={index} gutter={[16, 8]}>
                    <Col span={5}>
                        <Typography variant="T20">{label}</Typography>
                    </Col>
                    <Col>
                        <Typography variant="T20">{value}</Typography>
                    </Col>
                </Row>
            ))}

            {/* Contract Information Header */}
            <Typography
                variant="T20"
                style={{paddingTop: '10px', fontSize: '14px', textDecoration: 'underline'}}
            >
                Contract Information
            </Typography>

            {/* Table of Contents */}
            <div style={{marginBottom: 24}}>
                {reportTOC.map(({position, title}, idx) => (
                    <div
                        key={idx}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 16,
                            marginBottom: 10,
                        }}
                    >
                        <span style={{width: 40, textAlign: 'left', flexShrink: 0}}>{position}</span>
                        <span style={{marginLeft: 50}}>{title}</span>
                    </div>
                ))}
            </div>


            {/* Clearance Memo Report TOC */}
            {treeData?.map(section => renderSection(section))}
        </div>
    );
}
